import { useState } from 'react';
import { TTransactionsFilter } from '@xeppt/xeppt-sdk/types/transaction';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiAdminService } from '@api';
import { getDateOneMonthAgo, removeEmptyArrays } from '@utils/index';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useLocales } from '@hooks/helpers/useLocales';
import { modalIds } from '@const/modals';
import useModalContext from '@hooks/context/useModalContext';
import { TCursor } from '@xeppt/xeppt-sdk/types';
import {
    TConsumerCardTransactionResponseWithReferenceCode,
    TConsumerCardTransactionsWithReferenceCode,
    TTransactionLiteWithReferenceCode,
    TTransactionWithReferenceCode
} from '@reference_code';

interface IProps {
    isInitialTransactions?: boolean;
    customer_id?: string;
    card_id?: string;
}

export const useTransactions = ({ customer_id, isInitialTransactions, card_id }: IProps) => {
    const [cursor, setCursor] = useState<Date>();
    const [cardCursor, setCardCursor] = useState<Date>(new Date());
    const [transactions, setTransactions] = useState<TTransactionLiteWithReferenceCode[]>([]);
    const [cardTransactions, setCardTransactions] = useState<
        TConsumerCardTransactionResponseWithReferenceCode[]
    >([]);
    const [filter, setFilter] = useState<TTransactionsFilter>({
        id: undefined,
        action: undefined,
        method: undefined,
        status: undefined,
        customer_id: undefined,
        //@ts-ignore
        date_from: undefined,
        //@ts-ignore
        date_to: undefined,
        //@ts-ignore
        amount_from: undefined,
        search: undefined,
        //@ts-ignore
        amount_to: undefined,
        order: 'DESC'
    });
    const { requestErrorLocale } = useLocales();
    const [cardTransactionsFilter, setCardTransactionsFilter] = useState({
        end_date: new Date(),
        start_date: getDateOneMonthAgo()
    });
    const { onOpen, onClose } = useModalContext();

    const { data: transactionsData, handleRequest: refetchTransactions } = useApiQuery<
        any,
        TCursor<TTransactionLiteWithReferenceCode>
    >({
        method: (request?: any) => {
            return apiAdminService.getTransactions(
                //@ts-ignore
                { ...removeEmptyArrays(request?.filter), customer_id },
                { cursor: request?.isNew ? undefined : cursor, limit: 100 }
            );
        },
        onSuccess: (data, request?: any) => {
            if (cursor !== data.cursor && cursor && !request?.isNew) {
                setTransactions((state) => [...state, ...data.data]);
                setCursor(data.cursor);
            } else {
                setTransactions(data.data);
                setCursor(data.cursor);
            }
        },
        isInitialRequest: isInitialTransactions,
        deps: [customer_id]
    });

    const onChangeFilter = (data: Partial<typeof filter>) => {
        setCursor(undefined);
        setFilter((state) => ({ ...state, ...data }));
        refetchTransactions({ isNew: true, filter: { ...filter, ...data } });
    };

    const {
        data: transaction,
        handleRequest: getSingleTransaction,
        handleRemoveResponse: handleRemoveTransactionData
    } = useApiQuery<string, TTransactionWithReferenceCode>({
        method: (id?: string) => apiAdminService.getTransactionById(id || ''),
        isInitialRequest: false
    });

    const { data: cardTransactionsData, handleRequest: handleRequestCardTransaction } = useApiQuery<
        any,
        TConsumerCardTransactionsWithReferenceCode
    >(
        {
            method: (request?: any) =>
                apiAdminService.getConsumerCardTransactions({
                    user_id: customer_id as string,
                    card_id: card_id || '',
                    ...cardTransactionsFilter,
                    cursor: request?.isNew ? new Date() : cardCursor,
                    limit: 100,
                    order: filter.order
                }),
            onSuccess: (data, request?: any) => {
                if (cursor !== data.cursor && cursor && !request?.isNew) {
                    setCardTransactions((state) => [...state, ...data.data]);
                    setCardCursor(data.cursor);
                } else {
                    setCardTransactions(data.data);
                    setCardCursor(data.cursor);
                }
            },
            isInitialRequest: false
        }
    );

    const { handleRequest: handleRefundTransaction } = useApiMutation({
        method: (id: string) => apiAdminService.refundTransaction(id),
        onSuccess: () => {
            refetchTransactions({ isNew: true, filter: { ...filter, customer_id } });
            onClose();
        },
        onError: requestErrorLocale
    });

    const handleOpenRefundModal = (id: string) => {
        onOpen({
            modalId: modalIds.REFUND_TRANSACTION,
            onSubmit: () => handleRefundTransaction(id)
        });
    };

    return {
        transactions,
        filter,
        refetchTransactions,
        transactionsData,
        onChangeFilter,
        transaction,
        getSingleTransaction,
        handleRemoveTransactionData,
        cardTransactions,
        handleChangeCardTransactionsFilter: (value: { start_date: Date; end_date: Date }) => {
            setCardTransactionsFilter(value);
        },
        handleRequestCardTransaction,
        cardTransactionsFilter,
        handleOpenRefundModal,
        cardTransactionsData
    };
};
